import { useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { ProgressBar } from '@/components/ui/loading';
import type { TableAction, TableColumn } from '@/components/ui/tables/Table.types';
import type { RecalculationProcessStatus, RecalculoProcessDetail } from '@/api/domains/recalculation';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';
import { useUI } from '@/hooks/ui/useUI';
import { useExport } from '@/hooks/ui/useExport';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import {
  PENDING_STATUS,
  IN_PROGRESS_STATUS,
  ERROR_STATUS,
  FINISHED_STATUS,
  STOPPED_STATUS,
} from '@/hooks/attendance/recalculation/useRecalculationStatus';


interface UseLastProcessTableResult {
  columns: TableColumn<RecalculationProcessStatus>[];
  actions: TableAction<RecalculationProcessStatus>[];
  tableData: RecalculationProcessStatus[];
  isExporting: boolean;
}

export function useLastProcessTable(
  processStatus: RecalculationProcessStatus | null,
): UseLastProcessTableResult {
  const { t: translate } = useTranslation();
  const { openSidePanel } = useUI();
  const { canView } = usePagePermissions();

  const { onExport: downloadLastErrorReport, isExporting } = useExport({
    exportFn: () => recalculationMonthlyCutsService.downloadLastErrorReport(),
    successMessage: translate('attendance:recalculo.ultimoProceso.messages.exportSuccess'),
    errorMessage: translate('attendance:recalculo.ultimoProceso.messages.exportError'),
  });

  const columns = useMemo<TableColumn<RecalculationProcessStatus>[]>(() => [
    {
      key: 'monthlyCutId',
      header: translate('attendance:recalculo.ultimoProceso.columns.processId'),
    },
    {
      key: 'monthlyCutYear',
      header: translate('attendance:recalculo.ultimoProceso.columns.cutYear'),
    },
    {
      key: 'monthlyCutMonth',
      header: translate('attendance:recalculo.ultimoProceso.columns.cutMonth'),
    },
    {
      key: 'totalEmployees',
      header: translate('attendance:recalculo.ultimoProceso.columns.employees'),
    },
    {
      key: 'completedEmployees',
      header: translate('attendance:recalculo.ultimoProceso.columns.processed'),
    },
    {
      key: 'executionDate',
      header: translate('attendance:recalculo.ultimoProceso.columns.executionDate'),
    },
    {
      key: 'adminName',
      header: translate('attendance:recalculo.ultimoProceso.columns.executedBy'),
    },
    {
      key: 'status',
      header: translate('attendance:recalculo.ultimoProceso.columns.status'),
    },
    {
      key: 'progressPercentage',
      header: '',
      width: 160,
      render: (row) =>
        row.status === PENDING_STATUS || row.status === IN_PROGRESS_STATUS ? (
          <ProgressBar
            value={row.progressPercentage}
            animated
            data-testid="last-process-progress-bar"
          />
        ) : null,
    },
  ], [translate]);

  const actions = useMemo<TableAction<RecalculationProcessStatus>[]>(() => {
    if (!canView) return [];

    return [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: (row) => {
          openSidePanel({
            title: translate('attendance:recalculo.detalleProceso.title'),
            component: 'RecalculoDetailPanel',
            props: {
              processData: {
                processId: row.monthlyCutId,
                cutYear: row.monthlyCutYear,
                cutMonth: row.monthlyCutMonth,
                cutDescription: row.cutDescription,
                processedCompanies: row.processedCompanies,
                totalEmployees: row.totalEmployees,
                processedEmployees: row.completedEmployees,
                executionDate: row.executionDate,
                completionDate: row.completionDate,
                executedBy: row.adminName,
                status: row.status,
                stoppedBy: row.stoppedBy,
              } satisfies RecalculoProcessDetail,
            },
          });
        },
        hidden: (row) =>
          row.status !== ERROR_STATUS
          && row.status !== FINISHED_STATUS
          && row.status !== STOPPED_STATUS,
      },
      {
        id: 'download',
        label: translate('common:tableActions.download'),
        icon: 'export',
        onClick: () => { void downloadLastErrorReport(); },
        hidden: (row) => row.status !== ERROR_STATUS && row.status !== STOPPED_STATUS,
        disabled: () => isExporting,
      },
    ];
  }, [canView, translate, openSidePanel, downloadLastErrorReport, isExporting]);

  const tableData = useMemo(
    () => (canView && processStatus ? [processStatus] : []),
    [canView, processStatus],
  );

  return { columns, actions, tableData, isExporting };
}

export default useLastProcessTable;
